<template>
    <div>
        <kgr-alert sticky />
        <kgr-confirm ref="confirmUnsavedChanges">
            {{ $t('kindergarten.feeTables.unsavedChanges') }}<br /><br />
            {{ $t('question.doYouWantToProceed') }}
        </kgr-confirm>
        <kgr-confirm ref="confirmAdjustingServiceEntries">
            {{ $t('warning.changesEffectServiceEntries', { num: adjustingServiceEntriesNew, numDelete: adjustingServiceEntriesDelete }) }}<br />
            <br />
            <div
                v-if="adjustingServiceEntriesAllowNegative"
                class="kgr-text--bold"
            >
                {{ $t('warning.changesEffectServiceEntriesWarning') }}
            </div>
            <br />
            {{ $t('question.doYouWantToProceed') }}
        </kgr-confirm>
        <div v-if="!editFeeTableHistory">
            <div class="kgr-row">
                <kgr-button
                    icon-add
                    class="kgr-button kgr-float--right"
                    @click="showCreateHistoryForm()"
                >
                    {{
                        $t('kindergarten.feeTable.createHistory')
                    }}
                </kgr-button><br />
            </div>
            <br />
            <br />
            <kgr-loading v-if="loading" />
            <kgr-empty
                v-else-if="feeTableHistories.length === 0"
                :value="$t('kindergarten.feeTable.noData')"
            />
            <table
                v-else
                class="kgr-table kgr-table--block kgr-table--striped"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline bordered">
                            {{ $t('kindergarten.feeTable.name') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline bordered">
                            {{ $t('Beginn') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline bordered">
                            {{ $t('kindergarten.feeTable.ageRange.min') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline bordered">
                            {{ $t('bis') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="feeTableHistory in feeTableHistories"
                        :key="feeTableHistory.id"
                        class="kgr-table__row kgr-table"
                    >
                        <td class="kgr-table__cell">
                            {{ feeTableHistory.name }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ feeTableHistory.start }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ feeTableHistory.ageRange.min }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ feeTableHistory.ageRange.max }}
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-icon-edit @click="selectFeeTableHistory(feeTableHistory)" />
                            <kgr-delete-dialog
                                :value="confirmDeleteId === feeTableHistory.id"
                                :object-name="feeTableHistory.name"
                                @post-input="onDeleteDialogInput"
                                @confirm="deleteFeeTableHistory(feeTableHistory)"
                            >
                                <kgr-icon-delete
                                    :disabled="!feeTableHistory.deletable"
                                    :title="deleteNote(feeTableHistory)"
                                    @click="showDeleteFeeTableHistoryDialog(feeTableHistory)"
                                />
                            </kgr-delete-dialog>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <template v-else>
            <kgr-form-scaffold
                :save-button-disabled="invalidFeeTableHistory"
                :pending="loading"
                @primaryBtnClicked="editFeeTableHistory.isNew() ? createFeeTableHistory() : update()"
                @backBtnClicked="editFeeTableHistory = null;loadHistories()"
            >
                <h3
                    v-if="editFeeTableHistory.isNew()"
                    v-t="'kindergarten.feeTable.createHistory'"
                    class="kgr-headline--h3"
                ></h3><br />
                <h3
                    v-if="!editFeeTableHistory.isNew()"
                    v-t="editFeeTableHistory.name"
                    class="kgr-headline--h3"
                ></h3><br />

                <div class="kgr-row">
                    <div class="kgr-col">
                        <kgr-text-input
                            v-model="editFeeTableHistory.name"
                            :label="$t('kindergarten.feeTable.name.mandatory')"
                            :error="feeTableHistoryError.messageForField('name')"
                            @post-input="onInput"
                        />
                    </div>
                </div>
                <div class="kgr-row">
                    <div class="kgr-col">
                        <kgr-number-input
                            v-model="editFeeTableHistory.ageRange.min"
                            :label="$t('kindergarten.feeTable.ageRange.min.mandatory')"
                            :disabled="!editFeeTableHistory.isNew()"
                            :error="feeTableHistoryError.messageForField('ageRange.min')"
                            @post-input="onInput"
                        />
                    </div>
                    <div class="kgr-col">
                        <kgr-number-input
                            v-model="editFeeTableHistory.ageRange.max"
                            :label="$t('kindergarten.feeTable.ageRange.max.mandatory')"
                            :disabled="!editFeeTableHistory.isNew()"
                            :error="feeTableHistoryError.messageForField('ageRange.max')"
                            @post-input="onInput"
                        />
                    </div>
                    <div
                        v-if="impersonalAccountEnabled"
                        class="kgr-col"
                    >
                        <kgr-text-input
                            v-model="editFeeTableHistory.impersonalAccount"
                            :label="$t('kindergarten.feeTable.impersonalAccount')"
                        />
                    </div>
                    <div
                        v-if="receivableTypeEnabled"
                        class="kgr-col"
                    >
                        <kgr-text-input
                            v-model="editFeeTableHistory.receivableType"
                            :label="$t('kindergarten.feeTable.receivableType')"
                            max-length="10"
                            :error="feeTableHistoryError.messageForField('receivableType')"
                        />
                    </div>
                    <div
                        v-if="billOfFeesEnabled"
                        class="kgr-col"
                    >
                        <kgr-bill-of-fees-select v-model="editFeeTableHistory.fee" />
                    </div>
                </div>
                <div class="kgr-row">
                    <kgr-note-box
                        v-if="editFeeTableHistory.isNew()"
                        text-key="kindergarten.feeTable.createNote"
                    />
                </div>

                <kgr-fee-tables
                    :fee-table-history="editFeeTableHistory"
                    :error="feeTableHistoryError"
                    @current-fee-table="onCurrentFeeTable"
                />
            </kgr-form-scaffold>
        </template>
    </div>
</template>

<script>
import BillOfFeesSelect from '../common/form/select/BillOfFeesSelect';
import ConfirmDialog from '../common/modal/ConfirmDialog';
import DeleteDialog from '../common/modal/DeleteDialog';
import EmptyData from '../common/EmptyData';
import Error from '../../js/model/Error';
import FeeTableHistory from '../../js/dto/FeeTableHistory';
import FeeTableHistoryRepository from '../../js/repository/FeeTableHistoryRepository';
import FeeTables from '../../components/kindergarten/FeeTables';
import FormScaffold from '../../components/common/form/FormScaffold';
import IconDelete from '../common/icon/IconDelete';
import IconEdit from '../common/icon/IconEdit';
import KgrButton from '../common/form/button/KgrButton';
import KgrNumberInput from '../common/form/input/NumberInput';
import Loading from '../common/Loading';
import NoteBox from '../../components/common/NoteBox';
import TextInput from '../common/form/input/TextInput';
import Vue from 'vue';

export default {
    components: {
        'kgr-number-input': KgrNumberInput,
        'kgr-fee-tables': FeeTables,
        'kgr-form-scaffold': FormScaffold,
        'kgr-icon-delete': IconDelete,
        'kgr-icon-edit': IconEdit,
        'kgr-button': KgrButton,
        'kgr-text-input': TextInput,
        'kgr-bill-of-fees-select': BillOfFeesSelect,
        'kgr-loading': Loading,
        'kgr-empty': EmptyData,
        'kgr-confirm': ConfirmDialog,
        'kgr-delete-dialog': DeleteDialog,
        'kgr-note-box': NoteBox,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
        impersonalAccountEnabled: {
            type: Boolean,
            required: true,
        },
        receivableTypeEnabled: {
            type: Boolean,
            required: true,
        },
        billOfFeesEnabled: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            confirmDeleteId: null,
            adjustingServiceEntriesNew: 0,
            adjustingServiceEntriesDelete: 0,
            adjustingServiceEntriesAllowNegative: 0,
            loading: false,
            feeTableHistories: [],
            currentFeeTable: null,
            editFeeTableHistory: null,
            feeTableHistoryError: new Error(),
        };
    },
    computed: {
        invalidFeeTableHistory() {
            return false; // space for improved error handling
        },
    },
    mounted() {
        this.loadHistories();
    },
    methods: {
        onDeleteDialogInput() {
            this.confirmDeleteId = null;
        },
        showDeleteFeeTableHistoryDialog(feeTableHistory) {
            this.confirmDeleteId = feeTableHistory.id;
        },
        deleteFeeTableHistory(feeTableHistory) {
            this.loading = true;
            FeeTableHistoryRepository.delete(feeTableHistory.id)
                .then(() => {
                    this.loading = false;
                    this.loadHistories();
                    this.$notify.success('saveChanges.success');
                })
                .catch((error) => {
                    this.loading = false;
                    this.handleError(error);
                });
        },
        deleteNote(feeTableHistory) {
            return feeTableHistory.deletable ? '' : this.$t('kindergarten.feeTable.historyNotDeletable');
        },
        selectFeeTableHistory(feeTableHistory) {
            this.$notify.hideAll();
            this.editFeeTableHistory = feeTableHistory;
        },
        onCurrentFeeTable(currentFeeTable) {
            this.currentFeeTable = currentFeeTable;
        },
        resetFeeTableHistoryError() {
            this.feeTableHistoryError = new Error();
            Vue.set(this, 'feeTableHistoryError', new Error());
        },
        loadHistories() {
            this.loading = true;
            this.feeTableHistories = [];
            this.editFeeTableHistory = null;
            this.$notify.hideAll();
            this.scrollToTop();
            return FeeTableHistoryRepository.findByKindergarten(this.kindergartenId)
                .then((feeTableHistories) => {
                    this.resetFeeTableHistoryError();
                    this.feeTableHistories = feeTableHistories;
                    this.loading = false;
                })
                .catch(() => this.loading = false);
        },
        handleError(apiError) {
            this.feeTableHistoryError = apiError;
            if (apiError.messageForField('feeTable.values')) {
                this.$notify.error(apiError.messagesForField('feeTable.values'));
            } else if (apiError.messageForField('feeTable')) {
                this.$notify.error(apiError.messagesForField('feeTable'));
            } else if (apiError.messageForField('feeTableHistory')) {
                this.$notify.error(apiError.messagesForField('feeTableHistory'));
            } else {
                this.$notify.error('saveChanges.error');
            }
        },
        showCreateHistoryForm() {
            this.editFeeTableHistory = FeeTableHistory.init(this.kindergartenId);
        },
        confirmUnsavedChangesIfRequired() {
            return new Promise((resolve, reject) => {
                const hasUnsavedChanges = this.editFeeTableHistory.feeTables.filter((feeTable) => feeTable.hasChanged() && this.currentFeeTable !== feeTable).length > 0;

                if (!hasUnsavedChanges) {
                    resolve();
                    return;
                }

                this.$refs.confirmUnsavedChanges.open().then((confirmed) => {
                    if (confirmed) {
                        resolve();
                    } else {
                        reject();
                    }
                });
            });
        },
        async update() {
            this.confirmUnsavedChangesIfRequired()
                .then(this.fetchAdustingServiceEntries)
                .then(this.confirmAdustingServiceEntriesIfRequired)
                .then(this.updateFeeTableHistory)
                .then(() => {
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    if (error) {
                        this.handleError(error);
                    }
                });
        },
        confirmAdustingServiceEntriesIfRequired(response) {
            return new Promise((resolve, reject) => {
                if (response.data.proceed === true) {
                    resolve();
                    return;
                }

                this.adjustingServiceEntriesNew = response.data.newAdjustingServiceEntries;
                this.adjustingServiceEntriesDelete = response.data.deleteAdjustingServiceEntries;
                this.adjustingServiceEntriesAllowNegative = response.data.allowNegativeAmountBySubventions;

                this.$refs.confirmAdjustingServiceEntries.open().then((confirmed) => {
                    if (confirmed) {
                        resolve();
                    } else {
                        reject();
                    }
                });
            });
        },
        fetchAdustingServiceEntries() {
            this.loading = true;
            return FeeTableHistoryRepository.precheckAdjustingServiceEntries(this.editFeeTableHistory, this.currentFeeTable);
        },
        updateFeeTableHistory() {
            this.loading = true;
            return FeeTableHistoryRepository.update(this.editFeeTableHistory, this.currentFeeTable)
                .then(() => {
                    this.editFeeTableHistory = null;
                    this.resetFeeTableHistoryError();
                    this.loadHistories();
                    this.$notify.success('saveChanges.success');
                });
        },
        createFeeTableHistory() {
            this.loading = true;
            FeeTableHistoryRepository.create(this.kindergartenId, this.editFeeTableHistory, this.currentFeeTable)
                .then((response) => {
                    const feeTableHistoryId = response.headers['x-identifier'];
                    this.editFeeTableHistory = null;
                    this.resetFeeTableHistoryError();
                    this.loadHistories()
                        .then(() => {
                            this.$notify.success('saveChanges.success');
                            this.editFeeTableHistory = this.feeTableHistories.find((feeTableHistory) => feeTableHistory.id === feeTableHistoryId);
                        })
                        .catch((error) => this.handleError(error));
                })
                .catch((error) => {
                    this.handleError(error);
                    this.loading = false;
                });
        },
        onInput() {
            this.resetFeeTableHistoryError();
            this.validName();
            this.validAge();
            this.$forceUpdate();
        },
        validName() {
            this.feeTableHistoryError.clear('name');
            if (this.editFeeTableHistory.name == null || this.editFeeTableHistory.name.trim() === '') {
                this.feeTableHistoryError.add('name', this.$t('kindergarten.feeTable.name.empty'));
            } else if (this.feeTableHistories.find((feeTableHistory) => feeTableHistory.name === this.editFeeTableHistory.name && this.editFeeTableHistory.id !== feeTableHistory.id) != null) {
                this.feeTableHistoryError.add('name', this.$t('kindergarten.feeTable.name.notUnique'));
            }
        },
        validAge() {
            this.feeTableHistoryError.clear('ageRange.min');
            this.feeTableHistoryError.clear('ageRange.max');
            if (isNaN(this.editFeeTableHistory.ageRange.min) || isNaN(this.editFeeTableHistory.ageRange.max)) {
                if (isNaN(this.editFeeTableHistory.ageRange.min)) {
                    this.feeTableHistoryError.add('ageRange.min', this.$t('kindergarten.feeTable.mustNotBeEmpty'));
                }
                if (isNaN(this.editFeeTableHistory.ageRange.max)) {
                    this.feeTableHistoryError.add('ageRange.max', this.$t('kindergarten.feeTable.mustNotBeEmpty'));
                }
                return;
            }
            if (this.editFeeTableHistory.ageRange.min >= this.editFeeTableHistory.ageRange.max) {
                this.feeTableHistoryError.add('ageRange.min', this.$t('range.shouldBeGreaterThanMin'));
            }
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.kgr-table__cell--headline.bordered {
    border-right: 1px solid white;
}
</style>
